import gql from 'graphql-tag';

import {assert} from 'Asserts';
import {ReviewLifecycleType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {teamHasValidNonAuthorReviewersForReviewable} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import * as logging from 'util/Logging';

import {
  ReviewLifecycleSummaryPanelQuery,
  ReviewLifecycleSummaryPanelQueryVariables,
} from './__generated/ReviewLifecycleSummaryPanelQuery';
import {deriveCyclicalPanelContext} from './cyclicalReviewBadgeDisplayState';
import {deriveLinearPanelContext} from './linearReviewBadgeDisplayState';
import {ReviewBadgeContext} from './ReviewBadgeContext';
import {
  findLatestAcceptanceSnapshot,
  findSnapshotForCurrentReviewLifecycle,
  getAuthorAction,
  getScopedReviewLifecycleVersion,
  type ReviewChangeGraphQL,
  type ReviewerGraphQL,
  type ReviewLifecycleGraphQL,
} from './reviewBadgeShared';
import {Snapshot} from './ReviewConstants';

export const REVIEW_LIFECYCLE_SUMMARY_PANEL_QUERY = gql`
  query ReviewLifecycleSummaryPanelQuery($reviewableId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        authors {
          id
          handle
          name
          avatarUrl
        }
        review {
          id
          status
          activeReviewLifecycleVersion {
            id
            lifecycleType
            reviewStages {
              id
              name
              selectedTeam {
                id
                name
                members {
                  id
                  name
                }
              }
              allowAuthorReviewers
            }
            reviewLifecycle {
              id
              currentVersion {
                id
                name
              }
              organization {
                id
                teams {
                  id
                  name
                }
              }
            }
          }
          reviewChanges {
            id
            createdAt
            newStatus
            creator {
              ... on CreatorInterface {
                id
                name
                handle
                avatarUrl
              }
              ... on UserNode {
                id
                email
              }
            }
            previousStage {
              id
              name
              action
              sequentialPositionIndex
            }
            wasEsigned
            action
            message
            snapshot {
              id
              createdAt
              status
              url
            }
            wasCreatedBySnapshot
            reviewItemVersionApiIdentifier
            reviewLifecycleVersion {
              id
              lifecycleType
              reviewLifecycle {
                id
                currentVersion {
                  id
                  name
                }
              }
            }
          }
          reviewers {
            id
            reviewStatus
            updatedAt
            isUserValidReviewer
            user {
              ...UserAvatarInfoGraphQL
            }
            reviewStage {
              id
              name
              action
              description
              sequentialPositionIndex
              selectedTeam {
                id
                name
                members {
                  id
                  name
                }
                adminsJson
              }
              isTeamReassignable
              allowAuthorReviewers
              allowSenderReviewer
            }
            isTeamReassignable
          }
          reviewSender {
            id
          }
        }
        reviewSource {
          id
        }
      }
      ... on EntryNode {
        id
        template {
          id
          templateCollection {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('UserAvatarInfo').fragment}
`;

type ReviewLifecycleSummaryOrInaccessibleGraphQL = ReviewLifecycleSummaryPanelQuery['reviewable'];

type ReviewableGraphQL = ExcludeInaccessible<ReviewLifecycleSummaryOrInaccessibleGraphQL>;

type ReviewGraphQL = NonNullable<ReviewableGraphQL['review']>;

type ActiveReviewLifecycleVersionGraphQL = NonNullable<ReviewGraphQL['activeReviewLifecycleVersion']>;

export interface ReviewSummaryPanelReadyState {
  state: 'READY';
  reviewable: ReviewableGraphQL;
  review: ReviewGraphQL;
  authors: ReviewableGraphQL['authors'];
  status: ReviewStatus;
  reviewChanges: Array<ReviewChangeGraphQL>;
  reviewers: Array<ReviewerGraphQL>;
  activeReviewLifecycleVersion: ActiveReviewLifecycleVersionGraphQL | null;
  reviewLifecycle: ReviewLifecycleGraphQL | null;
  reviewProcessName: string | null;
  lifecycleType: ReviewLifecycleType;
  reviewSenderId: string | undefined;
  hasComments: boolean;
  snapshot: Snapshot | null;
  acceptanceSnapshot: Snapshot | null;
  isSelfReview: boolean;
  maybeAuthorAction: ReturnType<typeof getAuthorAction>;
  missingTeamReviewerStages: Array<string>;
  actionsEnabled: boolean;
  isViewingActiveCycle: boolean;
  scopedStatus: ReviewStatus | null;
  refetch: () => void;
}

export type ReviewSummaryPanelResult = {state: 'PENDING'} | {state: 'HIDDEN'} | ReviewSummaryPanelReadyState;

function deriveReviewSummaryPanelReadyState(
  reviewable: ReviewableGraphQL,
  openVersionId: string | undefined,
  isCurrentEditableDraft: boolean | undefined,
  refetch: () => void
): ReviewSummaryPanelReadyState | {state: 'HIDDEN'} {
  const {review, authors} = reviewable;

  assert(
    review != null,
    'Attempting to render a ReviewLifecycleSummaryPanel for a reviewable item that does not have an attached review'
  );

  const {status, reviewChanges, reviewers, activeReviewLifecycleVersion, reviewSender} = review;
  const reviewSenderId = reviewSender ? reviewSender.id : undefined;

  const {scopedReviewChanges, scopedStatus, isActiveCycleVersion, panelStatus} =
    openVersionId != null
      ? deriveCyclicalPanelContext({openVersionId, globalStatus: status, reviewChanges})
      : deriveLinearPanelContext({globalStatus: status, reviewChanges});

  const hasComments = scopedReviewChanges.some(
    (change) => change.message != null && change.message.trim() !== ''
  );

  const snapshot = findSnapshotForCurrentReviewLifecycle(scopedReviewChanges);
  const acceptanceSnapshot = findLatestAcceptanceSnapshot(scopedReviewChanges);

  // Stamped review changes carry the RLC version for this cycle, including when
  // review.activeReviewLifecycleVersion is null.
  const scopedReviewLifecycleVersion = getScopedReviewLifecycleVersion(scopedReviewChanges);

  // Active-cycle rendering requires live reviewers and activeReviewLifecycleVersion.
  // Without them, fall back to scoped status/changes and stamped RLC info only — not reviewer-stage steps.
  const hasLiveActiveCycleData = activeReviewLifecycleVersion != null && reviewers != null;
  const isViewingActiveCycle =
    isActiveCycleVersion &&
    hasLiveActiveCycleData &&
    (openVersionId == null || scopedStatus !== ReviewStatus.RETRACTED) &&
    (openVersionId == null || isCurrentEditableDraft === true);

  // Linear (non-versioned) reviewables always render their live active cycle.
  if (openVersionId == null) {
    assert(activeReviewLifecycleVersion != null, 'Trying to view review summary without RLC version');
    assert(reviewers != null, 'Trying to view review summary without configured reviewers');
  }

  const actionsEnabled = isViewingActiveCycle && panelStatus !== ReviewStatus.CYCLE_COMPLETED;

  const effectiveReviewLifecycleVersion = isViewingActiveCycle
    ? activeReviewLifecycleVersion
    : (scopedReviewLifecycleVersion ?? activeReviewLifecycleVersion);
  if (effectiveReviewLifecycleVersion == null) {
    logging.error('Attempting to render a ReviewLifecycleSummaryPanel without a review lifecycle version');
    return {state: 'HIDDEN'};
  }

  const {lifecycleType} = effectiveReviewLifecycleVersion;
  const isSelfReview = lifecycleType === ReviewLifecycleType.SELF_REVIEW;
  const reviewProcessName = effectiveReviewLifecycleVersion.reviewLifecycle.currentVersion.name;

  // The full review lifecycle (with stages/teams) is only needed for the interactive active-cycle actions,
  // so it is only available from `activeReviewLifecycleVersion`.
  const reviewLifecycle = activeReviewLifecycleVersion?.reviewLifecycle ?? null;

  const maybeAuthorAction =
    actionsEnabled && reviewLifecycle != null ? getAuthorAction(panelStatus, reviewLifecycle) : null;

  const authorIds = authors?.map((author) => author.id) ?? [];
  const missingTeamReviewerStages =
    isViewingActiveCycle && activeReviewLifecycleVersion != null
      ? activeReviewLifecycleVersion.reviewStages
          .filter(({selectedTeam, allowAuthorReviewers}) => {
            if (selectedTeam == null || allowAuthorReviewers) {
              return false;
            }
            const memberIds = selectedTeam.members.map((member) => member.id);
            return !teamHasValidNonAuthorReviewersForReviewable(memberIds, authorIds);
          })
          .map(({name}) => name)
      : [];

  const panelReviewers = isViewingActiveCycle ? (reviewers ?? []) : [];

  return {
    state: 'READY',
    reviewable,
    review,
    authors,
    status: panelStatus,
    reviewChanges: [...scopedReviewChanges],
    reviewers: panelReviewers,
    activeReviewLifecycleVersion,
    reviewLifecycle,
    reviewProcessName,
    lifecycleType,
    reviewSenderId,
    hasComments,
    snapshot,
    acceptanceSnapshot,
    isSelfReview,
    maybeAuthorAction,
    missingTeamReviewerStages,
    actionsEnabled,
    isViewingActiveCycle,
    scopedStatus,
    refetch,
  };
}

/**
 * Data controller for the review lifecycle summary panel. Owns the (network-only) panel query and the
 * derivation the panel renders from (snapshot, author action, missing team reviewers). Runs inside the
 * panel component (which only mounts when the dropdown opens), preserving the lazy, fresh-on-open fetch.
 */
export function useReviewSummaryPanel(reviewableId: string): ReviewSummaryPanelResult {
  const {openVersionId, isCurrentEditableDraft} = ReviewBadgeContext.useContext();

  const result = useBenchlingQuery<
    ReviewLifecycleSummaryPanelQuery,
    ReviewLifecycleSummaryPanelQueryVariables
  >(REVIEW_LIFECYCLE_SUMMARY_PANEL_QUERY, {variables: {reviewableId}, fetchPolicy: 'network-only'});

  if (result.type === QueryResultType.PENDING) {
    return {state: 'PENDING'};
  }
  if (result.type === QueryResultType.ERROR) {
    return {state: 'HIDDEN'};
  }

  const {reviewable} = result.data;

  if (isInaccessible(reviewable)) {
    logging.error('User did not have access to the reviewable item while fetching review lifecycle data');
    return {state: 'HIDDEN'};
  }

  return deriveReviewSummaryPanelReadyState(reviewable, openVersionId, isCurrentEditableDraft, () => {
    result.refetch();
  });
}
